import { useAuthActions } from "@convex-dev/auth/react";
import {
  createRootRoute,
  Link,
  Outlet,
  useNavigate,
} from "@tanstack/react-router";
import { api } from "@repo/backend";
import {
  AuthLoading,
  Authenticated,
  Unauthenticated,
  useQuery,
} from "convex/react";
import { useState } from "react";

export const Route = createRootRoute({
  component: RootLayout,
});

function RootLayout() {
  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <AppNavbar />
      <main className="flex-1">
        <Outlet />
      </main>
    </div>
  );
}

function AppNavbar() {
  const navigate = useNavigate();
  const { signOut } = useAuthActions();
  const currentUser = useQuery(api.users.queries.me);
  const [isSigningOut, setIsSigningOut] = useState(false);

  async function handleSignOut() {
    setIsSigningOut(true);
    try {
      await signOut();
      await navigate({ to: "/" });
    } finally {
      setIsSigningOut(false);
    }
  }

  return (
    <nav className="sticky top-0 z-50 border-b border-iron-800 bg-iron-950/90 backdrop-blur-md">
      <div className="container mx-auto max-w-7xl px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group">
          <div className="w-1 h-8 bg-secondary shadow-[0_0_8px_rgba(241,198,41,0.6)] group-hover:shadow-secondary/80 transition-shadow" />
          <span className="font-epic tracking-[0.3em] text-iron-100 text-sm uppercase group-hover:text-white transition-colors">
            TGC Auction
          </span>
        </Link>

        <div className="flex items-center gap-6">
          <Link
            to="/"
            className="text-[10px] tracking-[0.3em] uppercase font-bold text-iron-400 hover:text-iron-100 transition-colors [&.active]:text-secondary"
            activeOptions={{ exact: true }}
          >
            Auction Hall
          </Link>
          <Link
            to="/cards/new"
            className="text-[10px] tracking-[0.3em] uppercase font-bold text-iron-400 hover:text-iron-100 transition-colors [&.active]:text-secondary"
          >
            Submit Listing
          </Link>
          <Link
            to="/ds"
            className="text-[10px] tracking-[0.3em] uppercase font-bold text-iron-400 hover:text-iron-100 transition-colors [&.active]:text-secondary"
          >
            Design System
          </Link>
          <div className="flex items-center gap-2 border border-iron-700 px-4 h-8 hover:border-secondary/40 hover:bg-iron-800/40 transition-all cursor-pointer mr-2">
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse shadow-[0_0_5px_rgba(205,1,1,0.8)]" />
            <span className="text-[9px] tracking-[0.25em] uppercase font-bold text-iron-300">
              Live
            </span>
          </div>

          <div className="w-px h-6 bg-iron-800" />

          <AuthLoading>
            <div className="flex items-center gap-3 border border-iron-800 px-3 h-8 text-[10px] tracking-[0.2em] uppercase font-bold text-iron-500">
              Checking Gate
            </div>
          </AuthLoading>

          <Unauthenticated>
            <Link
              to="/sign-in"
              className="text-[10px] tracking-[0.3em] uppercase font-bold text-iron-400 hover:text-iron-100 transition-colors [&.active]:text-secondary"
            >
              Sign In
            </Link>
            <Link
              to="/register"
              className="flex items-center justify-center px-4 h-8 bg-secondary/10 text-secondary border border-secondary/30 hover:bg-secondary/20 hover:border-secondary/50 transition-all text-[10px] tracking-[0.2em] font-bold uppercase"
            >
              Register
            </Link>
          </Unauthenticated>

          <Authenticated>
            <Link to="/profile" className="flex items-center gap-3 border border-iron-700 bg-iron-900/70 px-3 h-8 hover:border-secondary/40 hover:bg-iron-800/40 transition-all cursor-pointer">
              <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-iron-100">
                {currentUser?.name ?? currentUser?.email ?? "Adventurer"}
              </span>
            </Link>
            <div className="flex items-center gap-3 border border-iron-700 bg-iron-900/70 px-3 h-8">
              <span className="text-[9px] tracking-[0.25em] uppercase font-bold text-iron-500">
                Rupiah
              </span>
              <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-secondary">
                {currentUser?.goldBalance ?? 0} rp
              </span>
            </div>
            <button
              type="button"
              onClick={() => void handleSignOut()}
              disabled={isSigningOut}
              className="flex items-center justify-center px-4 h-8 border border-primary/30 bg-primary/10 text-primary hover:bg-primary/20 hover:border-primary/50 disabled:cursor-not-allowed disabled:opacity-60 transition-all text-[10px] tracking-[0.2em] font-bold uppercase"
            >
              {isSigningOut ? "Leaving" : "Sign Out"}
            </button>
          </Authenticated>
        </div>
      </div>
    </nav>
  );
}
